import type { LVPosition, LVSektion } from "@/components/project/leistungsverzeichnis-kalkulator";
import type { CalculationPosition } from "@shared/calculation-template";

export type TemplateOfferPosition = {
  code: string; name: string; unit: string; ep: number; section: string; templateId: string;
  calculation?: CalculationPosition;
  sectionTitle?: string;
};
export const templateOfferPositionId = (position: TemplateOfferPosition) =>
  `custom-k7-${position.templateId}-${position.code}`;

/** Keep photo-driven standard rows hidden, but retain all deliberate project
 * additions, including manual rows added beside imported K7 snapshots. */
export function calculationVisibleSections(sections: LVSektion[], visibleIds?: string[]): LVSektion[] {
  if (!visibleIds) return sections;
  return sections.map((section) => visibleIds.includes(section.id) ? section : {
    ...section, positionen: section.positionen.filter((position) => position.id.startsWith("custom-")),
  }).filter((section) => visibleIds.includes(section.id) || section.positionen.length > 0);
}

/** Explicitly copy prices into the offer, never maintain a live price link.
 * Reapplying a template updates the existing snapshot without duplicating quantities. */
export function applyCalculationTemplate(sections: LVSektion[], positions: TemplateOfferPosition[]): LVSektion[] {
  const previous = new Map(sections.flatMap((section) => section.positionen.map((position) => [position.id, position] as const)));
  const ids = new Set(positions.map(templateOfferPositionId));
  const copies = positions.map((position) => {
    if (!sections.some((section) => section.id === position.section)) throw new Error("Die Leistungsgruppe dieser Vorlagenposition ist im Angebot nicht vorhanden.");
    if (position.unit === "ohne Einheit" || !position.unit.trim()) throw new Error("Bitte die fehlende Einheit zuerst in der Vorlage ergänzen.");
    if (!Number.isFinite(position.ep)) throw new Error("Ungültiger Kalkulationspreis");
    if (position.sectionTitle !== undefined && (!position.sectionTitle.trim() || position.sectionTitle.trim().length > 150)) {
      throw new Error("Ungültige Gruppenüberschrift in der Kalkulationsvorlage.");
    }
    const id = templateOfferPositionId(position);
    const existing = previous.get(id);
    const copy: LVPosition = {
      ...existing, id, name: `${position.code} ${position.name}`, unit: position.unit, ep: position.ep,
      qty: existing?.qty ?? 1, aktiv: existing?.aktiv ?? true, library: false,
      calculation: position.calculation ? structuredClone(position.calculation) : existing?.calculation,
    };
    return { section: position.section, copy };
  });
  return sections.map((section) => ({
    ...section,
    titel: positions.find((position) => position.section === section.id && position.sectionTitle)?.sectionTitle?.trim() || section.titel,
    positionen: [
      ...section.positionen.filter((position) => !ids.has(position.id)),
      ...copies.filter((position) => position.section === section.id).map(({ copy }) => copy),
    ],
  }));
}
